import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useSearchFscHouseholdsQuery,
  useListFscFirmsQuery,
  useListFscRepsQuery,
  useGetFscSyncStatusQuery,
} from '../../app/api';
import { useIsMobile } from '../utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const PAGE_SIZE = 50;

// '#' catches names that do not start with a letter (trusts, "3M Retirees"),
// so the strip can never hide households from the operator.
const LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

function letterStyle(active) {
  return {
    minWidth: 28, padding: '0.15rem 0.4rem', fontSize: '0.78rem', fontWeight: 700,
    borderRadius: 4, cursor: 'pointer',
    border: `1px solid ${active ? '#0a2947' : 'rgba(10,41,71,0.15)'}`,
    background: active ? '#0a2947' : 'white',
    color: active ? 'white' : '#58585b',
  };
}

/**
 * Work-side client book, backed by Salesforce FSC.
 *
 * Reads the local `fsc_households` cache rather than Salesforce directly, so
 * type-ahead search across 10,030 households is a SQL query rather than a SOQL
 * call per keystroke. The cache is refreshed on a schedule; the footer shows
 * when, so the page is honest about its own staleness instead of implying it is
 * live.
 *
 * Document counts come from the migrated corpus joined through
 * `crm_identity_crosswalk` — documents carry SALENTICA account ids while
 * households are FSC ids, so they only meet through the crosswalk.
 */
export default function FscClients() {
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [firm, setFirm] = useState('');
  const [rep, setRep] = useState('');
  const [letter, setLetter] = useState('');
  // FSC labels households by FIRST name ("Thomas & Rebecca Alison"), so the A-Z
  // strip defaults to surname — that is how anyone actually looks a client up.
  // Sorting follows the same field, so the list reads in the order of the strip.
  const [letterField, setLetterField] = useState('last_name');
  const [withDocsOnly, setWithDocsOnly] = useState(false);
  const [page, setPage] = useState(0);

  // Reset to the first page whenever the filters change, otherwise a narrowed
  // search can land on an empty page and look like "no results".
  const onFilterChange = (setter) => (value) => { setter(value); setPage(0); };
  const onFirmChange = (value) => {
    setFirm(value);
    setRep('');   // the chosen advisor may not work at the newly-selected office
    setPage(0);
  };
  const hasFilters = Boolean(query || firm || rep || letter || withDocsOnly);
  const clearAll = () => {
    setQuery(''); setFirm(''); setRep(''); setLetter(''); setWithDocsOnly(false); setPage(0);
    // letterField is a view preference, not a filter — Clear leaves it alone.
  };

  const { data, isLoading, isFetching } = useSearchFscHouseholdsQuery({
    q: query || undefined,
    firm: firm || undefined,
    rep: rep || undefined,
    letter: letter || undefined,
    letter_field: letterField,
    with_docs_only: withDocsOnly,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const { data: firmsData } = useListFscFirmsQuery();
  // Advisors are scoped to the selected office, so picking a firm shortens the
  // advisor list rather than leaving all 139 in it.
  const { data: repsData } = useListFscRepsQuery({ firm: firm || undefined });
  const { data: sync } = useGetFscSyncStatusQuery();

  const households = data?.households || [];
  const total = data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firms = firmsData?.firms || [];
  const reps = repsData?.reps || [];

  const lastSynced = useMemo(() => {
    if (!sync?.last_synced) return null;
    const d = new Date(sync.last_synced.replace(' ', 'T') + 'Z');
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
  }, [sync]);

  const inputStyle = {
    border: '1px solid rgba(10,41,71,0.2)', borderRadius: 6,
    padding: '0.4rem 0.7rem', fontSize: '0.88rem', outline: 'none',
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <i className="bi bi-people-fill" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
            Salesforce Clients
          </h5>
          <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', marginLeft: 'auto' }}>
            {isFetching ? 'searching…' : `${total.toLocaleString()} household${total === 1 ? '' : 's'}`}
          </span>
        </div>

        <div style={{ padding: isMobile ? '0.75rem' : '1rem 1.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid rgba(10,41,71,0.08)' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => onFilterChange(setQuery)(e.target.value)}
            placeholder="Search client, advisor or office…"
            style={{ ...inputStyle, flex: '1 1 280px' }}
          />
          <select
            value={firm}
            onChange={(e) => onFirmChange(e.target.value)}
            aria-label="Office"
            style={{ ...inputStyle, flex: '0 1 240px' }}
          >
            <option value="">All offices</option>
            {firms.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select
            value={rep}
            onChange={(e) => onFilterChange(setRep)(e.target.value)}
            aria-label="Advisor"
            style={{ ...inputStyle, flex: '0 1 220px' }}
          >
            <option value="">All advisors</option>
            {reps.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: brand.deepBlue }}>
            <input
              type="checkbox"
              checked={withDocsOnly}
              onChange={(e) => onFilterChange(setWithDocsOnly)(e.target.checked)}
            />
            With documents only
          </label>
          {hasFilters && (
            <button type="button" onClick={clearAll} className="btn btn-sm btn-outline-secondary">
              Clear
            </button>
          )}
        </div>

        {/* The First/Last toggle sits on its own line above the letters because
            it changes what the strip means, rather than being another filter. */}
        <div style={{ padding: isMobile ? '0.5rem 0.75rem 0' : '0.6rem 1.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal }}>
            Jump to
          </span>
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(10,41,71,0.15)' }}>
            {[['last_name', 'Last name'], ['name', 'First name']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => { setLetterField(value); setPage(0); }}
                title={value === 'last_name'
                  ? "Filter and sort by the client's surname"
                  : 'Filter and sort by the household name as FSC labels it'}
                style={{
                  border: 'none', padding: '0.2rem 0.7rem', fontSize: '0.76rem', fontWeight: 700,
                  cursor: 'pointer',
                  background: letterField === value ? brand.tealBlue : 'white',
                  color: letterField === value ? 'white' : brand.charcoal,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* A-Z strip, matching the New Clients page. */}
        <div style={{ padding: isMobile ? '0.4rem 0.75rem 0.5rem' : '0.5rem 1.25rem 0.6rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid rgba(10,41,71,0.08)' }}>
          <button
            type="button"
            onClick={() => onFilterChange(setLetter)('')}
            style={letterStyle(letter === '')}
          >
            All
          </button>
          {LETTERS.map((L) => (
            <button
              key={L}
              type="button"
              onClick={() => onFilterChange(setLetter)(letter === L ? '' : L)}
              style={letterStyle(letter === L)}
            >
              {L}
            </button>
          ))}
        </div>

        <div style={{ padding: isMobile ? '0.5rem' : '0.5rem 1.25rem' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2.5rem' }}>
              <div className="spinner-border" style={{ color: brand.tealBlue }} />
            </div>
          ) : households.length === 0 ? (
            <p style={{ color: brand.charcoal, textAlign: 'center', padding: '2rem 0', margin: 0 }}>
              {query || firm || withDocsOnly
                ? 'No households match those filters.'
                : 'No households cached yet — run the FSC household sync.'}
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle" style={{ fontSize: '0.88rem', marginBottom: 0 }}>
                <thead>
                  <tr style={{ color: brand.charcoal, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <th>Client</th>
                    <th>Advisor</th>
                    <th>Office</th>
                    <th className="text-end">Documents</th>
                    <th>Portal</th>
                  </tr>
                </thead>
                <tbody>
                  {households.map((h) => (
                    <tr key={h.fsc_id}>
                      <td style={{ fontWeight: 600, color: brand.deepBlue }}>
                        <Link
                          to={`/work/clients/fsc/${h.fsc_id}`}
                          style={{ color: brand.deepBlue, textDecoration: 'none' }}
                        >
                          {h.name || '(unnamed)'}
                        </Link>
                      </td>
                      <td style={{ color: brand.charcoal }}>{h.rep_name || '—'}</td>
                      <td style={{ color: brand.charcoal }}>{h.rep_firm_name || '—'}</td>
                      <td className="text-end">
                        {h.doc_count > 0 ? (
                          <span style={{ fontWeight: 600 }}>{h.doc_count}</span>
                        ) : (
                          <span style={{ color: 'rgba(10,41,71,0.35)' }}>—</span>
                        )}
                      </td>
                      <td>
                        {h.has_portal ? (
                          <span style={{
                            background: brand.tealBlue, color: 'white', borderRadius: 4,
                            padding: '0.1rem 0.4rem', fontSize: '0.68rem', fontWeight: 700,
                            textTransform: 'uppercase',
                          }}>
                            Portal
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(10,41,71,0.35)', fontSize: '0.8rem' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div style={{ padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderTop: '1px solid rgba(10,41,71,0.08)' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <span style={{ color: brand.charcoal, fontSize: '0.82rem' }}>
              Page {page + 1} of {pageCount.toLocaleString()}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Be explicit that this is a cache, not live Salesforce. */}
      <p style={{ color: brand.charcoal, fontSize: '0.75rem', marginTop: '0.75rem', marginBottom: 0 }}>
        {sync?.households
          ? `${Number(sync.households).toLocaleString()} households from Salesforce FSC`
          : 'Salesforce FSC'}
        {lastSynced ? ` · last synced ${lastSynced}` : ''}
      </p>
    </div>
  );
}
